import React, { useState } from "react";
import { Button, Modal, Icon } from "semantic-ui-react";
import {
  searchDevices,
  SearchDevicesFiltersType,
} from "../../../../../BytebeamClient";
import { MessageContainer } from "./BulkMetadataUpdateModal";
import { saveAs } from "file-saver";
import LoadingAnimation from "../../../../common/Loader";
import styled from "styled-components";
import { AtLeastOnePropRequired } from "../../../../../util";
import { queryToObject } from "../../../common/QuerySelector";
import { useUser } from "../../../../../context/User.context";
import { Permission } from "../../../../../util";
import { downloadDevicesData } from "./DownloadMetadataModal";

export const CodeQueryText = styled.code`
  color: ${({ theme }) => theme.colors["code-query-text-color"]} !important;
  .codeQueryText {
    background: ${({ theme }) =>
      theme.colors["code-query-text-background-color"]} !important;
  }
`;

class FilterQueryText extends React.Component<{
  devicesSearchFilters: SearchDevicesFiltersType;
}> {
  render() {
    const { devicesSearchFilters } = this.props;

    const metadataFiltersString = devicesSearchFilters.metaDatafilters
      ? Object.entries(devicesSearchFilters.metaDatafilters)
          .map(([key, values]) => {
            if (Array.isArray(values)) {
              return `${key}: ${values.join(", ")}`;
            } else {
              return `${key} contains ${String(values)}`;
            }
          })
          .join(" AND \n")
      : "";

    return (
      <CodeQueryText id={"filter_query"} className="codeQueryText">
        <div className="codeQueryText" style={{ width: "fit-content" }}>
          {devicesSearchFilters.stateFilters &&
            `Device-Shadow Filters: ${queryToObject(
              devicesSearchFilters.stateFilters
            )
              .map(
                (filter) =>
                  `${filter.column} ${filter.operator} ${filter.value}`
              )
              .join(", ")}`}
        </div>
        <div
          style={{ marginTop: "5px", width: "fit-content" }}
          className="codeQueryText"
        >
          {metadataFiltersString &&
            `Metadata Filters - ${metadataFiltersString}`}
        </div>
      </CodeQueryText>
    );
  }
}

type DownloadFilteredMetadataModalProps = {
  close: (...args: any[]) => any;
  readonly devicesSearchFilters: SearchDevicesFiltersType;
  readonly isMetadataSelected: boolean;
  readonly isDeviceShadowDataSelected: boolean;
  readonly isLastActionDataSelected: boolean;
};

export function DownloadFilteredMetadataModal(
  props: DownloadFilteredMetadataModalProps
) {
  const {
    close,
    devicesSearchFilters,
    isMetadataSelected,
    isDeviceShadowDataSelected,
    isLastActionDataSelected,
  } = props;
  const { user } = useUser();
  const permissions: Permission = user.role.permissions;
  const [buttonDisabled, setButtonDisabled] = useState(false);

  async function getFilteredDevicesMetadata(): Promise<string> {
    try {
      const status = "active";
      const deviceFilters =
        devicesSearchFilters as AtLeastOnePropRequired<SearchDevicesFiltersType>;

      const devicesPerPage = 100000;
      const page = 1;

      // Get filtered devices
      const { devices } = await searchDevices(
        deviceFilters,
        page,
        devicesPerPage,
        status,
        null
      );

      // Process the filtered devices using our custom function
      const csvData = await downloadDevicesData(
        devices,
        permissions,
        isMetadataSelected,
        isDeviceShadowDataSelected,
        isLastActionDataSelected,
        false // filterForEditMetadata
      );

      return csvData;
    } catch (error) {
      console.error("Error While Fetching filtered devices data: ", error);
      throw error;
    }
  }

  async function downloadFilteredDevicesMetadata() {
    try {
      const csvData: BlobPart = await getFilteredDevicesMetadata();
      setButtonDisabled(false);

      const file = new File([csvData], "filtered_devices_data.csv", {
        type: "text/csv",
      });
      saveAs(file);
    } catch (error) {
      console.log("Error in downloading filtered device data: ", error);
    }
  }

  async function onClickDownloadMetadata() {
    setButtonDisabled(true);
    await downloadFilteredDevicesMetadata();
    close();
  }

  return (
    <>
      <Modal.Content>
        <Modal.Description>
          <MessageContainer>
            {buttonDisabled ? (
              <LoadingAnimation
                loadingText="Downloading filtered devices data..."
                loaderBorderSize="3px"
                loaderSize="30px"
                fontSize="14px"
              />
            ) : (
              <p>
                Download{" "}
                {[
                  isMetadataSelected && "metadata",
                  isDeviceShadowDataSelected && "device shadow data",
                  isLastActionDataSelected && "last action data",
                ]
                  .filter(Boolean)
                  .join(", ")}{" "}
                for filtered devices by clicking on the <b>Download</b> button.
                Alternatively, click on the <b>Back</b> button to return to the
                previous screen.
                <br></br>
                <FilterQueryText devicesSearchFilters={devicesSearchFilters} />
              </p>
            )}
          </MessageContainer>
        </Modal.Description>
      </Modal.Content>
      <Modal.Actions>
        <Button
          secondary
          onClick={() => {
            close();
          }}
        >
          <Icon name="angle left" /> Back
        </Button>
        {/* disable the button unless parsing and fetching is done */}
        <Button
          primary
          disabled={buttonDisabled}
          onClick={onClickDownloadMetadata}
        >
          <Icon name="checkmark" /> Download
        </Button>
      </Modal.Actions>
    </>
  );
}
